import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link } from "wouter";
import {
  AlertTriangle,
  Bell,
  Calendar,
  Check,
  CheckCircle2,
  Copy,
  FileText,
  KeyRound,
  Loader2,
  Plus,
  QrCode,
  RotateCw,
  ShieldCheck,
  Smartphone,
  Sparkles,
  Trash2,
  Wallet as WalletIcon,
  Zap,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DialCodeSelect } from "@/components/dial-code-select";
import { cn } from "@/lib/utils";
import { PageHead } from "@/components/bits";
import { useDashboard } from "@/features/dashboard/dashboard-context";
import { useI18n } from "@/i18n/i18n-context";
import { useToast } from "@/hooks/use-toast";
import { publicApi } from "@/services/api-client";
import {
  Empty,
  ErrorBox,
  Pager,
  SkeletonRows,
  Tag,
  buildQuery,
  fmtAmount,
  fmtWhen,
  pageInfo,
  pick,
  record,
  rows,
  text,
  useServer,
  type ApiRecord,
} from "@/features/finance/kit";

const PER_PAGE = 25;

type NotifTab = "plans" | "templates" | "whatsapp" | "history";

interface MerchantTemplate extends ApiRecord {
  id: string;
  code: string;
  channel: "EMAIL" | "SMS" | "WHATSAPP";
  language: "en" | "fr";
  subject?: string | null;
  body: string;
  brand_name?: string | null;
  active: boolean;
}

interface PlanItem {
  code: string;
  channels: string[];
  max_daily_notifications: number;
  max_daily_whatsapp: number;
  monthly_price_xaf: number;
  is_active?: boolean;
}

interface SubscriptionResponse {
  subscribed: boolean;
  subscription?: {
    id: string;
    account_id: string;
    plan: string;
    status: string;
    price_amount: number;
    currency: string;
    wallet_id?: string;
    auto_renew: boolean;
    starts_at: string;
    expires_at: string;
  } | null;
  quota_usage_today?: {
    total_sent: number;
    whatsapp_sent: number;
  };
}

interface WhatsAppStatusResponse {
  status:
    | "CONNECTED"
    | "QR_READY"
    | "PAIRING_CODE_READY"
    | "DISCONNECTED"
    | "CONNECTING"
    | "BANNED";
  connected: boolean;
  phoneNumber?: string;
  phone_number?: string;
  qrCode?: string;
  qr_data_url?: string;
  pairingCode?: string;
  pairing_code?: string;
}

export default function NotificationsPage() {
  const { t } = useI18n();
  const { isBusiness } = useDashboard();

  // Individual accounts can ONLY connect WhatsApp and view notifications.
  // Business accounts can manage plans, customer templates, WhatsApp and history.
  const [activeTab, setActiveTab] = useState<NotifTab>(() =>
    isBusiness ? "plans" : "whatsapp",
  );

  useEffect(() => {
    if (!isBusiness && activeTab === "plans") {
      setActiveTab("whatsapp");
    }
  }, [isBusiness, activeTab]);

  return (
    <>
      <PageHead title={t("notifications.title")} sub={t("notifications.sub")}>
        <Link
          href="/settings"
          className="text-xs font-semibold text-primary underline"
        >
          {t("notifications.preferences")}
        </Link>
      </PageHead>

      {/* Tabs Bar */}
      <div
        className="mb-6 flex flex-wrap items-center gap-2 border-b border-border pb-3"
        role="tablist"
        aria-label={t("notifications.title")}
      >
        {isBusiness && (
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "plans"}
            data-testid="tab-notif-plans"
            onClick={() => setActiveTab("plans")}
            className={cn(
              "inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-xs font-semibold transition-all cursor-pointer",
              activeTab === "plans"
                ? "bg-primary text-primary-foreground shadow-xs"
                : "text-muted-foreground hover:bg-muted hover:text-foreground",
            )}
          >
            <Sparkles className="h-4 w-4" />
            <span>{t("notifications.tabPlans")}</span>
          </button>
        )}

        {isBusiness && (
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "templates"}
            data-testid="tab-notif-templates"
            onClick={() => setActiveTab("templates")}
            className={cn(
              "inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-xs font-semibold transition-all cursor-pointer",
              activeTab === "templates"
                ? "bg-primary text-primary-foreground shadow-xs"
                : "text-muted-foreground hover:bg-muted hover:text-foreground",
            )}
          >
            <FileText className="h-4 w-4" />
            <span>{t("notifications.tabTemplates")}</span>
          </button>
        )}

        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "whatsapp"}
          data-testid="tab-notif-whatsapp"
          onClick={() => setActiveTab("whatsapp")}
          className={cn(
            "inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-xs font-semibold transition-all cursor-pointer",
            activeTab === "whatsapp"
              ? "bg-primary text-primary-foreground shadow-xs"
              : "text-muted-foreground hover:bg-muted hover:text-foreground",
          )}
        >
          <Smartphone className="h-4 w-4" />
          <span>{t("notifications.tabWhatsApp")}</span>
        </button>

        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "history"}
          data-testid="tab-notif-history"
          onClick={() => setActiveTab("history")}
          className={cn(
            "inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-xs font-semibold transition-all cursor-pointer",
            activeTab === "history"
              ? "bg-primary text-primary-foreground shadow-xs"
              : "text-muted-foreground hover:bg-muted hover:text-foreground",
          )}
        >
          <Bell className="h-4 w-4" />
          <span>{t("notifications.tabHistory")}</span>
        </button>
      </div>

      {/* Tab Contents */}
      {isBusiness && activeTab === "plans" && <PlansAndSubscriptionsTab />}
      {isBusiness && activeTab === "templates" && <CustomerTemplatesTab />}
      {activeTab === "whatsapp" && (
        <WhatsAppConnectionTab isBusiness={isBusiness} />
      )}
      {activeTab === "history" && <NotificationsInboxTab />}
    </>
  );
}

function CustomerTemplatesTab() {
  const { t } = useI18n();
  const { toast } = useToast();
  const query = useServer<ApiRecord[]>("/notifications/templates");
  const templates = useMemo(
    () =>
      query.data ? (rows(query.data) as unknown as MerchantTemplate[]) : [],
    [query.data],
  );
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [editing, setEditing] = useState<MerchantTemplate | null>(null);
  const [form, setForm] = useState({
    code: "",
    channel: "EMAIL" as MerchantTemplate["channel"],
    language: "fr" as MerchantTemplate["language"],
    subject: "",
    body: "",
    brand_name: "",
    active: true,
  });

  const openCreate = () => {
    setEditing(null);
    setForm({
      code: "",
      channel: "EMAIL",
      language: "fr",
      subject: "",
      body: "",
      brand_name: "",
      active: true,
    });
    setDialogOpen(true);
  };

  const openEdit = (template: MerchantTemplate) => {
    setEditing(template);
    setForm({
      code: template.code,
      channel: template.channel,
      language: template.language,
      subject: template.subject ?? "",
      body: template.body,
      brand_name: template.brand_name ?? "",
      active: template.active,
    });
    setDialogOpen(true);
  };

  const save = async (event: FormEvent) => {
    event.preventDefault();
    setSaving(true);
    try {
      await publicApi(
        editing
          ? `/notifications/templates/${editing.id}`
          : "/notifications/templates",
        {
          method: editing ? "PUT" : "POST",
          body: {
            ...form,
            subject: form.channel === "EMAIL" ? form.subject : null,
            brand_name: form.channel === "EMAIL" ? form.brand_name : null,
          },
        },
      );
      toast({ title: t("notifications.saveTemplate") });
      setDialogOpen(false);
      query.reload();
    } catch (error) {
      toast({
        title: "Erreur",
        description:
          error instanceof Error
            ? error.message
            : "Échec de sauvegarde du modèle.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (template: MerchantTemplate) => {
    if (!window.confirm(t("notifications.confirmDeleteTemplate"))) return;
    setDeleting(template.id);
    try {
      await publicApi(`/notifications/templates/${template.id}`, {
        method: "DELETE",
      });
      toast({ title: t("notifications.deleteTemplate") });
      query.reload();
    } catch (error) {
      toast({
        title: "Erreur",
        description:
          error instanceof Error
            ? error.message
            : "Échec de suppression du modèle.",
        variant: "destructive",
      });
    } finally {
      setDeleting(null);
    }
  };

  const channelLabel = (channel: MerchantTemplate["channel"]) =>
    channel === "EMAIL"
      ? t("notifications.templateEmail")
      : channel === "SMS"
        ? t("notifications.templateSms")
        : t("notifications.templateWhatsApp");

  return (
    <div className="space-y-5">
      <section className="rounded-2xl border bg-card p-5 shadow-xs sm:p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="max-w-3xl">
            <h2 className="text-lg font-bold">
              {t("notifications.templatesTitle")}
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              {t("notifications.templatesSub")}
            </p>
          </div>
          <Button onClick={openCreate}>
            <Plus className="mr-2 h-4 w-4" />
            {t("notifications.addTemplate")}
          </Button>
        </div>

        {query.error ? (
          <ErrorBox error={query.error} onRetry={query.reload} />
        ) : query.loading ? (
          <div className="mt-5">
            <SkeletonRows />
          </div>
        ) : templates.length === 0 ? (
          <div className="mt-5">
            <Empty
              icon={<FileText className="h-5 w-5" />}
              title={t("notifications.templateEmpty")}
            >
              {t("notifications.templateEmptySub")}
            </Empty>
          </div>
        ) : (
          <div className="mt-5 overflow-x-auto rounded-xl border border-border">
            <table className="w-full min-w-[620px] text-left text-sm">
              <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="px-4 py-3">
                    {t("notifications.templateCode")}
                  </th>
                  <th className="px-4 py-3">
                    {t("notifications.templateChannel")}
                  </th>
                  <th className="px-4 py-3">
                    {t("notifications.templateLanguage")}
                  </th>
                  <th className="px-4 py-3">
                    {t("notifications.templateActive")}
                  </th>
                  <th className="px-4 py-3 text-right">
                    {t("notifications.notificationDetails")}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {templates.map((template) => (
                  <tr key={template.id}>
                    <td className="px-4 py-3 font-mono font-semibold">
                      {template.code}
                    </td>
                    <td className="px-4 py-3">
                      {channelLabel(template.channel)}
                    </td>
                    <td className="px-4 py-3 uppercase">{template.language}</td>
                    <td className="px-4 py-3">
                      <span
                        className={cn(
                          "inline-flex rounded-full px-2.5 py-1 text-xs font-semibold",
                          template.active
                            ? "bg-emerald-50 text-emerald-700"
                            : "bg-muted text-muted-foreground",
                        )}
                      >
                        {template.active
                          ? t("notifications.templateActive")
                          : t("notifications.templateDisabled")}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openEdit(template)}
                        >
                          {t("notifications.editTemplate")}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={deleting === template.id}
                          onClick={() => void remove(template)}
                          aria-label={t("notifications.deleteTemplate")}
                        >
                          {deleting === template.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Trash2 className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editing
                ? t("notifications.editTemplate")
                : t("notifications.addTemplate")}
            </DialogTitle>
          </DialogHeader>
          <form className="space-y-4" onSubmit={save}>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="notification-template-code">
                  {t("notifications.templateCode")}
                </Label>
                <Input
                  id="notification-template-code"
                  value={form.code}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      code: event.target.value,
                    }))
                  }
                  required
                  pattern="[A-Za-z0-9][A-Za-z0-9._-]*"
                  maxLength={100}
                  disabled={Boolean(editing)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="notification-template-channel">
                  {t("notifications.templateChannel")}
                </Label>
                <select
                  id="notification-template-channel"
                  className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                  value={form.channel}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      channel: event.target
                        .value as MerchantTemplate["channel"],
                    }))
                  }
                >
                  <option value="EMAIL">
                    {t("notifications.templateEmail")}
                  </option>
                  <option value="SMS">{t("notifications.templateSms")}</option>
                  <option value="WHATSAPP">
                    {t("notifications.templateWhatsApp")}
                  </option>
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="notification-template-language">
                  {t("notifications.templateLanguage")}
                </Label>
                <select
                  id="notification-template-language"
                  className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                  value={form.language}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      language: event.target
                        .value as MerchantTemplate["language"],
                    }))
                  }
                >
                  <option value="fr">Français</option>
                  <option value="en">English</option>
                </select>
              </div>
              {form.channel === "EMAIL" && (
                <div className="space-y-2">
                  <Label htmlFor="notification-template-brand">
                    {t("notifications.templateBrand")}
                  </Label>
                  <Input
                    id="notification-template-brand"
                    value={form.brand_name}
                    onChange={(event) =>
                      setForm((current) => ({
                        ...current,
                        brand_name: event.target.value,
                      }))
                    }
                    maxLength={120}
                    required
                  />
                </div>
              )}
            </div>
            {form.channel === "EMAIL" && (
              <div className="space-y-2">
                <Label htmlFor="notification-template-subject">
                  {t("notifications.templateSubject")}
                </Label>
                <Input
                  id="notification-template-subject"
                  value={form.subject}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      subject: event.target.value,
                    }))
                  }
                  maxLength={255}
                  required
                />
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="notification-template-body">
                {t("notifications.templateBody")}
              </Label>
              <textarea
                id="notification-template-body"
                className="min-h-40 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={form.body}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    body: event.target.value,
                  }))
                }
                required
                maxLength={4000}
              />
              <p className="text-xs text-muted-foreground">
                {t("notifications.templateBodyHelp")}
              </p>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.active}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    active: event.target.checked,
                  }))
                }
              />
              {t("notifications.templateActive")}
            </label>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
              >
                {t("notifications.cancelTemplate")}
              </Button>
              <Button
                type="submit"
                disabled={saving || !form.code.trim() || !form.body.trim()}
              >
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {t("notifications.saveTemplate")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================================================================
// TAB 1: Plans and Subscriptions (BUSINESS ONLY)
// ============================================================================
function PlansAndSubscriptionsTab() {
  const { t } = useI18n();
  const { wallets, refresh: refreshOverview } = useDashboard();
  const { toast } = useToast();

  const subQuery = useServer<SubscriptionResponse>(
    "/notifications/subscription",
  );
  const plansQuery = useServer<PlanItem[]>("/notifications/plans");

  const [togglingAutoRenew, setTogglingAutoRenew] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [selectedPlanForModal, setSelectedPlanForModal] =
    useState<PlanItem | null>(null);
  const [subscribingWalletId, setSubscribingWalletId] = useState("");
  const [subscribingAutoRenew, setSubscribingAutoRenew] = useState(true);
  const [subscribingBusy, setSubscribingBusy] = useState(false);

  const subData = subQuery.data;
  const isSubscribed = Boolean(subData?.subscribed && subData?.subscription);
  const subscription = subData?.subscription;
  const usage = subData?.quota_usage_today ?? {
    total_sent: 0,
    whatsapp_sent: 0,
  };

  const plans = useMemo<PlanItem[]>(() => {
    if (!plansQuery.data) return [];
    if (Array.isArray(plansQuery.data)) return plansQuery.data;
    return rows(plansQuery.data) as unknown as PlanItem[];
  }, [plansQuery.data]);

  // Active plan details
  const activePlanDetails = useMemo(() => {
    if (!subscription?.plan) return null;
    return plans.find((p) => p.code === subscription.plan) ?? null;
  }, [subscription, plans]);

  // Set default wallet when modal opens
  useEffect(() => {
    if (selectedPlanForModal && wallets.length > 0 && !subscribingWalletId) {
      // Find wallet with sufficient balance, or first wallet
      const price = selectedPlanForModal.monthly_price_xaf;
      const match =
        wallets.find((w) => Number(w.balance) >= price) ?? wallets[0];
      setSubscribingWalletId(match?.id ?? "");
    }
  }, [selectedPlanForModal, wallets, subscribingWalletId]);

  const handleToggleAutoRenew = async (enable: boolean) => {
    setTogglingAutoRenew(true);
    try {
      await publicApi("/notifications/subscription/auto-renew", {
        method: "POST",
        body: { auto_renew: enable },
      });
      toast({
        title: t("notifications.autoRenewUpdated"),
        description: enable
          ? t("notifications.autoRenewEnabled")
          : t("notifications.autoRenewDisabled"),
      });
      subQuery.reload();
    } catch (err) {
      toast({
        title: "Erreur",
        description:
          err instanceof Error ? err.message : "Échec de mise à jour",
        variant: "destructive",
      });
    } finally {
      setTogglingAutoRenew(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!confirm(t("notifications.cancelSubscription") + " ?")) return;
    setCancelling(true);
    try {
      await publicApi("/notifications/subscription/cancel", { method: "POST" });
      toast({ title: t("notifications.subscriptionCancelled") });
      subQuery.reload();
      void refreshOverview();
    } catch (err) {
      toast({
        title: "Erreur",
        description:
          err instanceof Error ? err.message : "Échec de résiliation",
        variant: "destructive",
      });
    } finally {
      setCancelling(false);
    }
  };

  const handleConfirmSubscribe = async (e: FormEvent) => {
    e.preventDefault();
    if (!selectedPlanForModal || !subscribingWalletId || subscribingBusy)
      return;
    setSubscribingBusy(true);

    try {
      await publicApi("/notifications/subscribe", {
        method: "POST",
        body: {
          plan: selectedPlanForModal.code,
          wallet_id: subscribingWalletId,
          auto_renew: subscribingAutoRenew,
        },
      });

      toast({
        title: t("notifications.subscriptionSuccess"),
        description: `${selectedPlanForModal.code} (${selectedPlanForModal.monthly_price_xaf.toLocaleString()} XAF)`,
      });
      setSelectedPlanForModal(null);
      subQuery.reload();
      void refreshOverview();
    } catch (err) {
      toast({
        title: "Erreur de souscription",
        description:
          err instanceof Error ? err.message : "Échec de souscription au plan.",
        variant: "destructive",
      });
    } finally {
      setSubscribingBusy(false);
    }
  };

  const selectedWallet = wallets.find((w) => w.id === subscribingWalletId);
  const insufficientFunds =
    selectedWallet && selectedPlanForModal
      ? Number(selectedWallet.balance) < selectedPlanForModal.monthly_price_xaf
      : false;

  return (
    <div className="space-y-6">
      {/* SECTION 1: Active Subscription Overview */}
      <div className="rounded-2xl border bg-card p-6 shadow-xs">
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-border pb-4">
          <div>
            <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {t("notifications.activePlan")}
            </span>
            <div className="mt-1 flex items-center gap-3">
              <h2 className="text-2xl font-extrabold tracking-tight">
                {isSubscribed
                  ? subscription?.plan
                  : t("notifications.noActiveSubscription")}
              </h2>
              {isSubscribed && (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 px-3 py-0.5 text-xs font-bold text-emerald-600 dark:text-emerald-400">
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  Actif
                </span>
              )}
            </div>
          </div>

          {isSubscribed && subscription && (
            <div className="flex flex-wrap items-center gap-4">
              {/* Auto-renew Switch */}
              <div className="flex items-center gap-2 rounded-xl border border-border bg-muted/40 px-3 py-2">
                <Switch
                  id="auto-renew-toggle"
                  checked={subscription.auto_renew}
                  onCheckedChange={handleToggleAutoRenew}
                  disabled={togglingAutoRenew}
                />
                <Label
                  htmlFor="auto-renew-toggle"
                  className="cursor-pointer text-xs font-medium"
                >
                  {subscription.auto_renew
                    ? t("notifications.autoRenewEnabled")
                    : t("notifications.autoRenewDisabled")}
                </Label>
              </div>

              {/* Cancel Button */}
              <Button
                variant="outline"
                size="sm"
                onClick={handleCancelSubscription}
                disabled={cancelling}
                className="text-xs text-destructive hover:bg-destructive/10"
              >
                {cancelling ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  t("notifications.cancelSubscription")
                )}
              </Button>
            </div>
          )}
        </div>

        {/* Subscription details and Quota usages */}
        {isSubscribed && subscription ? (
          <div className="mt-5 grid gap-6 md:grid-cols-3">
            {/* Validity */}
            <div className="rounded-xl border border-border/60 bg-muted/20 p-4">
              <div className="flex items-center gap-2 text-xs font-semibold text-muted-foreground">
                <Calendar className="h-4 w-4" />
                <span>Validité</span>
              </div>
              <p className="mt-2 text-sm font-semibold">
                {t("notifications.expiresOn", {
                  date: fmtWhen(subscription.expires_at),
                })}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                Début : {fmtWhen(subscription.starts_at)}
              </p>
            </div>

            {/* Total notifications consumption */}
            <div className="rounded-xl border border-border/60 bg-muted/20 p-4">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-muted-foreground">
                  {t("notifications.totalNotificationsSent")}
                </span>
                <span className="font-mono text-xs font-bold">
                  {usage.total_sent} /{" "}
                  {activePlanDetails?.max_daily_notifications ?? 500}
                </span>
              </div>
              <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-muted">
                <div
                  className="h-full rounded-full bg-primary transition-all"
                  style={{
                    width: `${Math.min(
                      100,
                      (usage.total_sent /
                        (activePlanDetails?.max_daily_notifications || 500)) *
                        100,
                    )}%`,
                  }}
                />
              </div>
              <p className="mt-2 text-[11px] text-muted-foreground">
                Emails, SMS et WhatsApp réinitialisés chaque jour à minuit.
              </p>
            </div>

            {/* WhatsApp messages consumption */}
            <div className="rounded-xl border border-border/60 bg-muted/20 p-4">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-muted-foreground">
                  {t("notifications.whatsAppSent")}
                </span>
                <span className="font-mono text-xs font-bold">
                  {usage.whatsapp_sent} /{" "}
                  {activePlanDetails?.max_daily_whatsapp ?? 100}
                </span>
              </div>
              <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-muted">
                <div
                  className="h-full rounded-full bg-emerald-500 transition-all"
                  style={{
                    width: `${Math.min(
                      100,
                      (usage.whatsapp_sent /
                        (activePlanDetails?.max_daily_whatsapp || 100)) *
                        100,
                    )}%`,
                  }}
                />
              </div>
              <p className="mt-2 text-[11px] text-muted-foreground">
                Quota réservé à l'envoi direct via le worker WhatsApp.
              </p>
            </div>
          </div>
        ) : (
          <div className="mt-4 flex items-center gap-3 rounded-xl border border-amber-500/20 bg-amber-500/10 p-4 text-amber-900 dark:text-amber-200">
            <AlertTriangle className="h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400" />
            <p className="text-xs">
              {t("notifications.noActiveSubscriptionDesc")}
            </p>
          </div>
        )}
      </div>

      {/* SECTION 2: Available Plans Catalog */}
      <div>
        <div className="mb-4">
          <h3 className="text-lg font-bold tracking-tight">
            {t("notifications.availablePlans")}
          </h3>
          <p className="text-xs text-muted-foreground">
            Choisissez le forfait adapté à la volumétrie d'alertes clients de
            votre entreprise.
          </p>
        </div>

        {plansQuery.loading ? (
          <SkeletonRows n={3} />
        ) : plansQuery.error ? (
          <ErrorBox error={plansQuery.error} onRetry={plansQuery.reload} />
        ) : (
          <div className="grid gap-6 md:grid-cols-3">
            {plans.map((plan) => {
              const isCurrent = subscription?.plan === plan.code;
              const isPopular = plan.code === "PRO";

              return (
                <div
                  key={plan.code}
                  className={cn(
                    "relative flex flex-col justify-between rounded-2xl border bg-card p-6 shadow-sm transition-all hover:shadow-md",
                    isPopular && "border-primary ring-1 ring-primary/40",
                    isCurrent && "border-emerald-500 bg-emerald-500/5",
                  )}
                >
                  {isPopular && (
                    <div className="absolute -top-3 right-6 rounded-full bg-primary px-3 py-0.5 text-[10px] font-bold text-primary-foreground uppercase tracking-wider">
                      Populaire
                    </div>
                  )}

                  <div>
                    <div className="flex items-center justify-between">
                      <h4 className="text-xl font-bold tracking-tight">
                        {plan.code}
                      </h4>
                      {isCurrent && (
                        <span className="rounded-full bg-emerald-500/15 px-2.5 py-0.5 text-xs font-bold text-emerald-600 dark:text-emerald-400">
                          Actuel
                        </span>
                      )}
                    </div>

                    <div className="mt-4">
                      <span className="text-2xl font-extrabold">
                        {plan.monthly_price_xaf.toLocaleString()} XAF
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {" "}
                        / mois
                      </span>
                    </div>

                    <div className="my-5 border-t border-border" />

                    <div className="space-y-3 text-xs">
                      <div className="flex items-center gap-2">
                        <Check className="h-4 w-4 text-emerald-600" />
                        <span>
                          <strong>
                            {plan.max_daily_notifications.toLocaleString()}
                          </strong>{" "}
                          {t("notifications.maxDailyGlobal")}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Check className="h-4 w-4 text-emerald-600" />
                        <span>
                          <strong>
                            {plan.max_daily_whatsapp.toLocaleString()}
                          </strong>{" "}
                          {t("notifications.maxDailyWhatsApp")}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Check className="h-4 w-4 text-emerald-600" />
                        <span>{t("notifications.channelsIncluded")} :</span>
                      </div>
                      <div className="flex flex-wrap gap-1.5 pl-6">
                        {plan.channels.map((ch) => (
                          <span
                            key={ch}
                            className="rounded-md border border-border bg-muted/60 px-2 py-0.5 text-[10px] font-semibold"
                          >
                            {ch}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  <Button
                    className="mt-6 w-full"
                    variant={
                      isCurrent
                        ? "outline"
                        : isPopular
                          ? "default"
                          : "secondary"
                    }
                    disabled={isCurrent}
                    onClick={() => setSelectedPlanForModal(plan)}
                  >
                    {isCurrent
                      ? "Forfait souscrit"
                      : t("notifications.subscribeBtn")}
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Subscribe Dialog */}
      <Dialog
        open={Boolean(selectedPlanForModal)}
        onOpenChange={(open) => !open && setSelectedPlanForModal(null)}
      >
        <DialogContent className="sm:max-w-[480px]">
          {selectedPlanForModal && (
            <form onSubmit={handleConfirmSubscribe} className="space-y-5">
              <DialogHeader>
                <DialogTitle>
                  {t("notifications.subscribeModalTitle", {
                    plan: selectedPlanForModal.code,
                  })}
                </DialogTitle>
              </DialogHeader>

              <div className="rounded-xl border bg-muted/40 p-4">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">
                    Prix du forfait
                  </span>
                  <span className="font-mono text-base font-bold">
                    {selectedPlanForModal.monthly_price_xaf.toLocaleString()}{" "}
                    XAF
                  </span>
                </div>
                <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
                  <span>Quota global</span>
                  <span>
                    {selectedPlanForModal.max_daily_notifications} / jour
                  </span>
                </div>
                <div className="mt-1 flex items-center justify-between text-xs text-muted-foreground">
                  <span>Quota WhatsApp</span>
                  <span>{selectedPlanForModal.max_daily_whatsapp} / jour</span>
                </div>
              </div>

              {/* Wallet Selector */}
              <div className="space-y-1.5">
                <Label
                  htmlFor="sub-wallet-select"
                  className="text-xs font-semibold"
                >
                  {t("notifications.selectWallet")}
                </Label>
                <Select
                  value={subscribingWalletId}
                  onValueChange={setSubscribingWalletId}
                >
                  <SelectTrigger id="sub-wallet-select">
                    <SelectValue placeholder="Choisir un portefeuille" />
                  </SelectTrigger>
                  <SelectContent>
                    {wallets.map((w) => (
                      <SelectItem key={w.id} value={w.id}>
                        <div className="flex items-center justify-between gap-4">
                          <span>Portefeuille {w.currency}</span>
                          <span className="font-mono text-xs font-semibold">
                            {fmtAmount(w.balance, w.currency)}
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {insufficientFunds && (
                  <p className="flex items-center gap-1.5 text-xs text-destructive">
                    <AlertTriangle className="h-3.5 w-3.5" />
                    {t("notifications.insufficientFunds")}
                  </p>
                )}
              </div>

              {/* Auto Renew Checkbox */}
              <div className="flex items-center space-x-2 rounded-lg border border-border/60 p-3">
                <input
                  type="checkbox"
                  id="modal-auto-renew"
                  checked={subscribingAutoRenew}
                  onChange={(e) => setSubscribingAutoRenew(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                />
                <Label
                  htmlFor="modal-auto-renew"
                  className="cursor-pointer text-xs font-medium"
                >
                  {t("notifications.autoRenewCheckbox")}
                </Label>
              </div>

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setSelectedPlanForModal(null)}
                  disabled={subscribingBusy}
                >
                  Annuler
                </Button>
                <Button
                  type="submit"
                  disabled={
                    !subscribingWalletId || insufficientFunds || subscribingBusy
                  }
                >
                  {subscribingBusy ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Traitement…
                    </>
                  ) : (
                    t("notifications.confirmSubscribe")
                  )}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================================================================
// TAB 2: WhatsApp Connection Tab (BOTH INDIVIDUAL & BUSINESS)
// ============================================================================
function WhatsAppConnectionTab({ isBusiness }: { isBusiness: boolean }) {
  const { t } = useI18n();
  const { toast } = useToast();

  const statusQuery = useServer<WhatsAppStatusResponse>(
    "/whatsapp/session/status",
  );
  const [connectMode, setConnectMode] = useState<"QR" | "PAIRING_CODE">("QR");
  const [dialCode, setDialCode] = useState("237");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [busy, setBusy] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string | null>(null);
  const [pairingCodeResult, setPairingCodeResult] = useState<string | null>(
    null,
  );

  const statusData = statusQuery.data;
  const isConnected =
    statusData?.connected || statusData?.status === "CONNECTED";

  // Sync QR code / pairing code from status response if available
  useEffect(() => {
    const qr = statusData?.qrCode ?? statusData?.qr_data_url;
    const code = statusData?.pairingCode ?? statusData?.pairing_code;
    if (qr) {
      setQrCodeDataUrl(qr);
    }
    if (code) {
      setPairingCodeResult(code);
    }
  }, [statusData]);

  // Request QR Code connection
  const handleGenerateQr = async () => {
    setBusy(true);
    setPairingCodeResult(null);
    try {
      const res = await publicApi<{
        status: string;
        qrCode?: string;
        qr_data_url?: string;
      }>("/whatsapp/session/connect", {
        method: "POST",
        body: { mode: "QR" },
      });
      const qr = res.data?.qrCode ?? res.data?.qr_data_url;
      if (qr) {
        setQrCodeDataUrl(qr);
      }
      statusQuery.reload();
      toast({ title: t("notifications.statusQrReady") });
    } catch (err) {
      toast({
        title: "Erreur",
        description:
          err instanceof Error
            ? err.message
            : "Échec de génération du QR code.",
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  // Request Pairing Code connection
  const handleGeneratePairingCode = async (e: FormEvent) => {
    e.preventDefault();
    const cleanDigits = phoneNumber.replace(/\D/g, "");
    if (!cleanDigits || busy) return;
    setBusy(true);
    setQrCodeDataUrl(null);

    const cleanDial = dialCode.replace(/^\+/, "").trim() || "237";
    const fullPhone = `+${cleanDial}${cleanDigits}`;

    try {
      const res = await publicApi<{
        status: string;
        pairingCode?: string;
        pairing_code?: string;
        qrCode?: string;
        qr_data_url?: string;
      }>("/whatsapp/session/connect", {
        method: "POST",
        body: {
          mode: "PAIRING_CODE",
          phone_number: fullPhone,
        },
      });

      const code = res.data?.pairingCode ?? res.data?.pairing_code;
      if (code) {
        setPairingCodeResult(code);
      }
      statusQuery.reload();
      toast({ title: t("notifications.statusPairingReady") });
    } catch (err) {
      toast({
        title: "Erreur",
        description:
          err instanceof Error
            ? err.message
            : "Échec de génération du code de jumelage.",
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  // Disconnect session
  const handleDisconnect = async () => {
    if (!confirm(t("notifications.disconnectConfirm"))) return;
    setDisconnecting(true);
    try {
      await publicApi("/whatsapp/session/disconnect", { method: "POST" });
      setQrCodeDataUrl(null);
      setPairingCodeResult(null);
      statusQuery.reload();
      toast({ title: t("notifications.disconnectSuccess") });
    } catch (err) {
      toast({
        title: "Erreur",
        description:
          err instanceof Error ? err.message : "Échec de déconnexion.",
        variant: "destructive",
      });
    } finally {
      setDisconnecting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Information Banner */}
      <div className="rounded-2xl border border-border bg-card p-6 shadow-xs">
        <div className="flex items-start gap-4">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[#25D366]/15 text-[#25D366]">
            <Smartphone className="h-6 w-6" />
          </div>
          <div className="flex-1">
            <h3 className="text-base font-bold tracking-tight">
              {t("notifications.whatsAppTitle")}
            </h3>
            <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
              {t("notifications.whatsAppDesc")}
            </p>
            {!isBusiness && (
              <p className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-primary/10 px-3 py-1.5 text-xs font-semibold text-primary">
                <ShieldCheck className="h-4 w-4" />
                {t("notifications.whatsAppNoticeIndividual")}
              </p>
            )}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={statusQuery.reload}
            disabled={statusQuery.loading}
            className="gap-1.5"
          >
            <RotateCw
              className={cn(
                "h-3.5 w-3.5",
                statusQuery.loading && "animate-spin",
              )}
            />
            Actualiser
          </Button>
        </div>
      </div>

      {/* CONNECTED STATE */}
      {isConnected ? (
        <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-8 text-center shadow-xs">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-500/20 text-emerald-600 dark:text-emerald-400">
            <CheckCircle2 className="h-10 w-10" />
          </div>

          <h3 className="mt-4 text-xl font-bold tracking-tight">
            {t("notifications.statusConnected")}
          </h3>

          {statusData?.phoneNumber && (
            <p className="mt-2 font-mono text-sm font-semibold text-foreground">
              {t("notifications.connectedNumber")} : {statusData.phoneNumber}
            </p>
          )}

          <p className="mx-auto mt-2 max-w-md text-xs text-muted-foreground">
            Votre instance WhatsApp est active et prête à acheminer les codes
            OTP de vérification 2FA et vos notifications instantanées.
          </p>

          <div className="mt-6 flex justify-center">
            <Button
              variant="outline"
              size="sm"
              onClick={handleDisconnect}
              disabled={disconnecting}
              className="text-xs text-destructive hover:bg-destructive/10"
            >
              {disconnecting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Déconnexion…
                </>
              ) : (
                t("notifications.disconnectBtn")
              )}
            </Button>
          </div>
        </div>
      ) : (
        /* DISCONNECTED STATE: Pair via QR Code or Pairing Code */
        <div className="rounded-2xl border bg-card p-6 shadow-xs">
          {/* Pairing Mode Selector */}
          <div className="mb-6 flex gap-2 border-b border-border pb-3">
            <Button
              type="button"
              variant={connectMode === "QR" ? "default" : "outline"}
              size="sm"
              onClick={() => {
                setConnectMode("QR");
                if (!qrCodeDataUrl) handleGenerateQr();
              }}
              className="gap-2"
            >
              <QrCode className="h-4 w-4" />
              {t("notifications.qrModeTab")}
            </Button>

            <Button
              type="button"
              variant={connectMode === "PAIRING_CODE" ? "default" : "outline"}
              size="sm"
              onClick={() => setConnectMode("PAIRING_CODE")}
              className="gap-2"
            >
              <KeyRound className="h-4 w-4" />
              {t("notifications.pairingModeTab")}
            </Button>
          </div>

          {/* MODE 1: QR CODE */}
          {connectMode === "QR" && (
            <div className="grid gap-8 md:grid-cols-2 md:items-center">
              <div className="flex flex-col items-center justify-center rounded-xl border border-dashed p-6">
                {qrCodeDataUrl ? (
                  <div className="flex flex-col items-center">
                    <img
                      src={qrCodeDataUrl}
                      alt="WhatsApp QR Code"
                      className="h-64 w-64 rounded-lg bg-white p-2 shadow-sm"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleGenerateQr}
                      disabled={busy}
                      className="mt-4 gap-2 text-xs"
                    >
                      <RotateCw
                        className={cn("h-3.5 w-3.5", busy && "animate-spin")}
                      />
                      {t("notifications.refreshQrBtn")}
                    </Button>
                  </div>
                ) : (
                  <div className="py-12 text-center">
                    <QrCode className="mx-auto h-12 w-12 text-muted-foreground/60" />
                    <p className="mt-3 text-xs text-muted-foreground">
                      Cliquez ci-dessous pour générer un QR Code de connexion.
                    </p>
                    <Button
                      onClick={handleGenerateQr}
                      disabled={busy}
                      className="mt-4 gap-2"
                    >
                      {busy ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <QrCode className="h-4 w-4" />
                      )}
                      Générer le QR Code
                    </Button>
                  </div>
                )}
              </div>

              {/* Instructions */}
              <div className="space-y-4">
                <h4 className="text-base font-bold">
                  {t("notifications.scanQrTitle")}
                </h4>
                <ol className="space-y-3 text-xs text-muted-foreground">
                  <li className="flex items-start gap-2.5">
                    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 font-mono font-bold text-primary">
                      1
                    </span>
                    <span>
                      Ouvrez l'application <strong>WhatsApp</strong> sur votre
                      téléphone mobile.
                    </span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 font-mono font-bold text-primary">
                      2
                    </span>
                    <span>
                      Accédez à <strong>Réglages</strong> (iOS) ou au Menu{" "}
                      <strong>(⋮)</strong> &gt;{" "}
                      <strong>Appareils connectés</strong> (Android).
                    </span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 font-mono font-bold text-primary">
                      3
                    </span>
                    <span>
                      Touchez <strong>Connecter un appareil</strong> et scannez
                      le QR code affiché à gauche.
                    </span>
                  </li>
                </ol>
              </div>
            </div>
          )}

          {/* MODE 2: PAIRING CODE */}
          {connectMode === "PAIRING_CODE" && (
            <div className="grid gap-8 md:grid-cols-2 md:items-center">
              <div>
                <h4 className="text-base font-bold">
                  {t("notifications.pairingTitle")}
                </h4>
                <p className="mt-1 text-xs text-muted-foreground">
                  {t("notifications.pairingInstructions")}
                </p>

                <form
                  onSubmit={handleGeneratePairingCode}
                  className="mt-5 space-y-4"
                >
                  <div className="space-y-1.5">
                    <Label
                      htmlFor="whatsapp-phone"
                      className="text-xs font-semibold"
                    >
                      Numéro de téléphone WhatsApp
                    </Label>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <div className="sm:w-52 shrink-0">
                        <DialCodeSelect
                          value={dialCode}
                          onValueChange={setDialCode}
                          testId="select-whatsapp-dial-code"
                        />
                      </div>
                      <Input
                        id="whatsapp-phone"
                        type="tel"
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value)}
                        placeholder="Ex: 6XXXXXXXX"
                        required
                        className="flex-1 font-mono"
                      />
                    </div>
                    <p className="text-[11px] text-muted-foreground">
                      Numéro complet : +
                      {dialCode.replace(/^\+/, "").trim() || "237"}
                      {phoneNumber.replace(/\D/g, "")}
                    </p>
                  </div>

                  <Button
                    type="submit"
                    disabled={!phoneNumber || busy}
                    className="gap-2"
                  >
                    {busy ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <KeyRound className="h-4 w-4" />
                    )}
                    {t("notifications.generatePairingCode")}
                  </Button>
                </form>
              </div>

              {/* Pairing Code Display */}
              <div className="flex flex-col items-center justify-center rounded-xl border bg-muted/30 p-8 text-center">
                {pairingCodeResult ? (
                  <div className="space-y-3">
                    <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      {t("notifications.pairingCodeLabel")}
                    </span>
                    <div className="rounded-xl border border-primary/40 bg-card px-6 py-4 font-mono text-3xl font-extrabold tracking-widest text-primary shadow-xs">
                      {pairingCodeResult}
                    </div>
                    <div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          void navigator.clipboard?.writeText(
                            pairingCodeResult,
                          );
                          toast({ title: "Code copié dans le presse-papier" });
                        }}
                        className="gap-1.5 text-xs"
                      >
                        <Copy className="h-3.5 w-3.5" />
                        Copier le code
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {t("notifications.enterPairingPrompt")}
                    </p>
                  </div>
                ) : (
                  <div className="py-6 text-center">
                    <KeyRound className="mx-auto h-12 w-12 text-muted-foreground/50" />
                    <p className="mt-2 text-xs text-muted-foreground">
                      Le code à 8 chiffres s'affichera ici après validation de
                      votre numéro.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// TAB 3: Notifications Inbox (History)
// ============================================================================
function NotificationsInboxTab() {
  const { t } = useI18n();
  const { refresh: refreshOverview, markRead } = useDashboard();
  const [status, setStatus] = useState("all");
  const [type, setType] = useState("all");
  const [page, setPage] = useState(1);
  const [openId, setOpenId] = useState<string | null>(null);

  useEffect(() => setPage(1), [status, type]);
  const list = useServer<unknown>(
    buildQuery("/notifications", { status, type, per_page: PER_PAGE, page }),
  );
  const items = useMemo(() => rows(list.data), [list.data]);
  const info = pageInfo(list.data, list.meta, items.length, PER_PAGE);

  const isRead = (n: ApiRecord) =>
    Boolean(n.read_at ?? n.is_read) ||
    text(n, "status").toUpperCase() === "READ";

  const handleOpenNotification = (n: ApiRecord) => {
    const id = text(n, "id");
    if (!id) return;
    setOpenId(id);
    void markRead(id);
  };

  return (
    <>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap gap-2">
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger
              className="h-9 w-40 bg-card"
              aria-label="Statut"
              data-testid="filter-notif-status"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">
                {t("notifications.allStatuses")}
              </SelectItem>
              <SelectItem value="UNREAD">
                {t("notifications.unread")}
              </SelectItem>
              <SelectItem value="READ">{t("notifications.read")}</SelectItem>
            </SelectContent>
          </Select>

          <Select value={type} onValueChange={setType}>
            <SelectTrigger
              className="h-9 w-44 bg-card"
              aria-label="Type"
              data-testid="filter-notif-type"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("notifications.allTypes")}</SelectItem>
              {["PAYMENT", "COMPLIANCE", "SYSTEM", "SECURITY"].map((tVal) => (
                <SelectItem key={tVal} value={tVal}>
                  {tVal}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Button
          variant="outline"
          size="sm"
          onClick={list.reload}
          className="gap-1.5"
          disabled={list.loading}
        >
          <RotateCw
            className={cn("h-3.5 w-3.5", list.loading && "animate-spin")}
          />
          {t("notifications.refresh")}
        </Button>
      </div>

      <div className="rise overflow-hidden rounded-xl border bg-card">
        {list.error ? (
          <ErrorBox className="m-4" error={list.error} onRetry={list.reload} />
        ) : list.loading && !items.length ? (
          <SkeletonRows />
        ) : items.length === 0 ? (
          <Empty
            icon={<Bell className="h-5 w-5" />}
            title={t("notifications.emptyNotices")}
          >
            {t("notifications.emptySub")}
          </Empty>
        ) : (
          <ul className="divide-y">
            {items.map((n) => {
              const subject =
                text(n, "rendered_subject") ||
                text(n, "title") ||
                t("notifications.title");
              const body =
                text(n, "rendered_body") || text(n, "body", "message");
              const statusVal = text(n, "status");

              return (
                <li key={text(n, "id")}>
                  <button
                    onClick={() => handleOpenNotification(n)}
                    data-testid={`row-notification-${text(n, "id")}`}
                    className="flex w-full items-start gap-3 px-4 py-3 text-left transition hover:bg-accent/40"
                  >
                    <span
                      className={cn(
                        "mt-1.5 h-2 w-2 shrink-0 rounded-full",
                        isRead(n) ? "bg-transparent" : "bg-primary",
                      )}
                      aria-label={
                        isRead(n)
                          ? t("notifications.read")
                          : t("notifications.unread")
                      }
                    />
                    <div className="min-w-0 flex-1">
                      <p
                        className={cn(
                          "truncate text-sm",
                          !isRead(n) && "font-bold",
                        )}
                      >
                        {subject}
                      </p>
                      <p className="line-clamp-2 text-xs text-muted-foreground">
                        {body}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      {statusVal && <Tag value={statusVal} />}
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
        {(items.length > 0 || page > 1) && (
          <Pager info={info} loading={list.loading} onPage={setPage} />
        )}
      </div>

      <Dialog
        open={Boolean(openId)}
        onOpenChange={(o) => {
          if (!o) {
            setOpenId(null);
            list.reload();
            void refreshOverview();
          }
        }}
      >
        <DialogContent className="sm:max-w-[480px]">
          {openId && <NotificationDetail id={openId} />}
        </DialogContent>
      </Dialog>
    </>
  );
}

export function NotificationDetail({ id }: { id: string }) {
  const { t } = useI18n();
  const res = useServer<unknown>(`/notifications/${encodeURIComponent(id)}`);
  const n = record(record(res.data).notification ?? res.data);
  if (res.error)
    return (
      <>
        <DialogTitle>{t("notifications.title")}</DialogTitle>
        <ErrorBox error={res.error} onRetry={res.reload} />
      </>
    );
  if (res.loading)
    return (
      <>
        <DialogTitle>{t("notifications.loadingTitle")}</DialogTitle>
        <SkeletonRows n={3} />
      </>
    );

  const subject =
    text(n, "rendered_subject") || text(n, "title") || t("notifications.title");
  const body = text(n, "rendered_body") || text(n, "body", "message");
  const statusVal = text(n, "status");

  return (
    <>
      <DialogHeader>
        <div className="flex items-center justify-between gap-2">
          <DialogTitle>{subject}</DialogTitle>
          {statusVal && <Tag value={statusVal} />}
        </div>
      </DialogHeader>
      <p className="whitespace-pre-line text-sm">{body}</p>
    </>
  );
}
